import React from "react";
import { useTranslation } from "react-i18next";
import { Column, colorVars, formatAvg } from "./contentTeamShared";
import HostAvatar from "./HostAvatar";
import styles from "./ContentTeam.module.css";

interface Props {
  columns: Column[];
  /** Teams in the division. */
  total: number;
  played: boolean;
}

/** One card per predictor for the shown division: average places off, exact hits, and who is best. */
const ScoreCards: React.FC<Props> = ({ columns, total, played }) => {
  const { t } = useTranslation("pages");
  const scored = columns.filter((c) => c.score);
  const best = scored.reduce<Column | null>((acc, c) => (!acc || c.score!.score < acc.score!.score ? c : acc), null);
  const tie = !!best && scored.filter((c) => Math.round(c.score!.score * 100) === Math.round(best.score!.score * 100)).length > 1;

  const sub = (c: Column): string => {
    if (c.loading) return "…";
    if (c.failed) return t("contentTeam.loadFailed");
    if (!c.ranks) return t("contentTeam.noPrediction");
    if (!played || !c.score) return t("contentTeam.notScored");
    return t("contentTeam.cardSub", { exact: c.score.exact, matched: c.matched, total });
  };

  return (
    <div className={`${styles.cards} ${columns.length > 3 ? styles.cardsMany : ""}`} style={{ "--cols": columns.length } as React.CSSProperties}>
      {columns.map((c) => (
        <div key={c.key} className={`${styles.panel} ${styles.card}`} style={colorVars(c.color)}>
          <HostAvatar steamId={c.steamId} name={c.name} color={c.color} size="md" />
          <div className={styles.cardText}>
            <span className={styles.cardName}>
              <span className={styles.cardNameText}>{c.name}</span>
              <span className={styles.cardTag}>{c.kind === "rival" ? c.team : t(c.kind === "host" ? "contentTeam.host" : "contentTeam.guest")}</span>
              {best && !tie && best.key === c.key && <span className={styles.bestBadge}>{t("contentTeam.best")}</span>}
            </span>
            <span className={styles.muted}>{sub(c)}</span>
          </div>
          <div className={styles.cardScore}>
            <span className={styles.cardAvg}>{played && c.score ? formatAvg(c.score.score) : "–"}</span>
            <span className={styles.statLabel}>{t("contentTeam.avgOff")}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ScoreCards;
